/**
 * App-wide context: language (RU/EN) + theme (light/dark) + accent + helpers.
 *
 * Split into two contexts sharing one `AppProvider`: `LangContext` (lang,
 * t/l/td — via `useApp()`) and `UiPrefsContext` (theme/accent/liveBg — via
 * `useUiPrefs()`). Each has its own memo, so toggling theme/accent doesn't
 * re-render every t()/l() consumer.
 *
 * `AppProvider` sits ABOVE `TamaguiProvider` (see app/_layout.tsx) so the theme
 * and accent toggles re-theme the whole portal. `t()` resolves a Localized
 * value; `l()` resolves a dotted string key from the dictionary.
 *
 * All three preferences persist via `src/lib/storage` (web: localStorage,
 * native: expo-secure-store). On web the initial state is read synchronously so
 * there's no flash; native hydrates on mount.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Platform } from 'react-native'

import { STR, LANGS, type Lang } from './strings'
import { AZ_DB } from './az-data'
import { setRuntimeLang } from './runtime-lang'
import { getItem, setItem } from '../lib/storage'
import { pluralRu } from '../lib/format'
import { ACCENT_KEYS, DEFAULT_ACCENT } from '../theme/accents'

export type { Lang } from './strings'
export { LANGS, LANG_LABELS } from './strings'
export type Localized = string | { ru: string; en: string; az?: string; ka?: string }
export type Theme = 'light' | 'dark'

const K_LANG = 'pref.lang'
const K_THEME = 'pref.theme'
const K_ACCENT = 'pref.accent'
const K_LIVEBG = 'pref.livebg'

export function tr(v: Localized | null | undefined, lang: Lang): string {
  if (v == null) return ''
  if (typeof v === 'object') {
    // Многие Localized-мапы в коде имеют только {ru,en}. Для az, если ключа нет,
    // пробуем словарь переводов по русской строке, прежде чем откатиться на ru.
    // (для ka словаря нет — сразу ru/en, иначе показали бы азербайджанский.)
    return v[lang] ?? (lang === 'az' && v.ru ? AZ_DB[v.ru] : undefined) ?? v.ru ?? v.en
  }
  return lang === 'az' ? AZ_DB[v] ?? v : v
}

/**
 * Форма строки по числу: варианты через «|» — ru: один|несколько|много, en: один|много,
 * az/ka — одна форма (после числительного существительное не меняется); `{n}` → число.
 */
export function plural(text: string, n: number, lang: Lang): string {
  const forms = text.split('|')
  const i = lang === 'ru' ? Number(pluralRu(n, '0', '1', '2')) : lang === 'en' && n !== 1 ? 1 : 0
  return forms[Math.min(i, forms.length - 1)].replace('{n}', String(n))
}

export interface AppState {
  lang: Lang
  setLang: (l: Lang) => void
  toggleLang: () => void
  /** translate a Localized value */
  t: (v: Localized | null | undefined) => string
  /** look up a dotted dictionary key */
  l: (key: string) => string
  /** dictionary key with plural forms (see plural) for count `n` */
  lp: (key: string, n: number) => string
  /**
   * Перевод строки, пришедшей с бэка/из БД (метки ЭДО, названия оргструктуры,
   * статические уведомления/ошибки), на AZ. Ключ — точная исходная строка; при
   * ru/en/ka и при отсутствии перевода строка возвращается как есть.
   */
  td: (s: string | null | undefined) => string
}

export interface UiPrefsState {
  theme: Theme
  setTheme: (t: Theme) => void
  toggleTheme: () => void
  /** selected accent palette key (see src/theme/accents.ts) */
  accent: string
  setAccent: (key: string) => void
  /** animated "live wallpaper" page background (see src/ui/LiveBackground) */
  liveBg: boolean
  setLiveBg: (on: boolean) => void
}

const LangContext = createContext<AppState | null>(null)
const UiPrefsContext = createContext<UiPrefsState | null>(null)

/** Synchronous read of a stored pref — web only (localStorage). */
function webPref(key: string): string | null {
  if (Platform.OS !== 'web') return null
  try {
    return globalThis.localStorage?.getItem(key) ?? null
  } catch {
    return null
  }
}

const isLang = (v: unknown): v is Lang => typeof v === 'string' && (LANGS as string[]).includes(v)
const initLang = (): Lang => {
  const v = webPref(K_LANG)
  return isLang(v) ? v : 'ru'
}
const initTheme = (): Theme => {
  const v = webPref(K_THEME)
  // Default to light on first entry; a saved preference still wins.
  return v === 'light' || v === 'dark' ? v : 'light'
}
const initAccent = (): string => {
  const v = webPref(K_ACCENT)
  return v && ACCENT_KEYS.includes(v) ? v : DEFAULT_ACCENT
}
// Live background is on by default; '0' = explicitly disabled.
const initLiveBg = (): boolean => webPref(K_LIVEBG) !== '0'

export function AppProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(initLang)
  const [theme, setTheme] = useState<Theme>(initTheme)
  const [accent, setAccent] = useState<string>(initAccent)
  const [liveBg, setLiveBg] = useState<boolean>(initLiveBg)
  // Web is already in sync from localStorage; native hydrates from secure-store.
  const [hydrated, setHydrated] = useState(Platform.OS === 'web')

  useEffect(() => {
    if (Platform.OS === 'web') return
    let alive = true
    ;(async () => {
      const [sl, st, sa, sb] = await Promise.all([
        getItem(K_LANG),
        getItem(K_THEME),
        getItem(K_ACCENT),
        getItem(K_LIVEBG),
      ])
      if (!alive) return
      if (isLang(sl)) setLang(sl)
      if (st === 'light' || st === 'dark') setTheme(st)
      if (sa && ACCENT_KEYS.includes(sa)) setAccent(sa)
      if (sb != null) setLiveBg(sb !== '0')
      setHydrated(true)
    })()
    return () => {
      alive = false
    }
  }, [])

  // Persist on change (only after hydration, so we never clobber stored prefs).
  useEffect(() => {
    if (hydrated) void setItem(K_LANG, lang)
  }, [lang, hydrated])
  useEffect(() => {
    if (hydrated) void setItem(K_THEME, theme)
  }, [theme, hydrated])
  useEffect(() => {
    if (hydrated) void setItem(K_ACCENT, accent)
  }, [accent, hydrated])
  useEffect(() => {
    if (hydrated) void setItem(K_LIVEBG, liveBg ? '1' : '0')
  }, [liveBg, hydrated])

  const langValue = useMemo<AppState>(
    () => ({
      lang,
      setLang,
      toggleLang: () => setLang((v) => LANGS[(LANGS.indexOf(v) + 1) % LANGS.length]),
      t: (v) => tr(v, lang),
      l: (key) => {
        const e = STR[key]
        return e ? e[lang] ?? e.ru : key
      },
      lp: (key, n) => {
        const e = STR[key]
        return plural(e ? e[lang] ?? e.ru : key, n, lang)
      },
      td: (s) => (s == null ? '' : lang === 'az' ? AZ_DB[s] ?? s : s),
    }),
    [lang],
  )

  const uiPrefsValue = useMemo<UiPrefsState>(
    () => ({
      theme,
      setTheme,
      toggleTheme: () => setTheme((v) => (v === 'light' ? 'dark' : 'light')),
      accent,
      setAccent: (key: string) => setAccent(ACCENT_KEYS.includes(key) ? key : DEFAULT_ACCENT),
      liveBg,
      setLiveBg,
    }),
    [theme, accent, liveBg],
  )

  // Зеркалим язык для не-React кода (чокпоинт ошибок API вне дерева компонентов).
  useEffect(() => {
    setRuntimeLang(lang)
  }, [lang])

  return (
    <LangContext.Provider value={langValue}>
      <UiPrefsContext.Provider value={uiPrefsValue}>{children}</UiPrefsContext.Provider>
    </LangContext.Provider>
  )
}

export function useApp(): AppState {
  const ctx = useContext(LangContext)
  if (!ctx) throw new Error('useApp must be used within <AppProvider>')
  return ctx
}

export function useUiPrefs(): UiPrefsState {
  const ctx = useContext(UiPrefsContext)
  if (!ctx) throw new Error('useUiPrefs must be used within <AppProvider>')
  return ctx
}
